/**
 * 분할결제 관리 API
 *
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-13
 */
import {
  CheckInstallmentAvailabilityDto,
  CreateCompanyPaymentSettingDto,
  CreateInstallmentPlanDto,
  CreateInstallmentPaymentHistoryDto,
  InstallmentAvailabilityResponseDto,
  InstallmentPlanDto,
  InstallmentPlanListQueryDto,
  InstallmentPlanListResponseDto,
  InstallmentPaymentHistoryDto,
  InstallmentPaymentHistoryListQueryDto,
  InstallmentPaymentHistoryListResponseDto,
  CompanyPaymentSettingDto,
  CompanyPaymentSettingListQueryDto,
  CompanyPaymentSettingListResponseDto,
  UpdateCompanyPaymentSettingDto,
  InstallmentPaymentStatisticsDto,
  MonthlyInstallmentSummaryDto,
  PaymentCompletionRateDto,
  // 누락된 DTO들 추가
  InstallmentHistoryDto,
  InstallmentHistoryListQueryDto,
  InstallmentStatsDto,
  InstallmentStatsQueryDto,
  ResultListDto,
  CompanyStampDto,
  SubscriptionProductDto,
} from "@/dto/InstallmentDto";
import instance from "./instance";
import { ResponseDto } from "@/dto/ResponseDto";
import { ENV } from "@/constants/env";
import axios from "axios";

// ===== 분할결제 계획 관리 API =====
const PLAN_PREFIX_PATH = `${ENV.PRE_FIX}/${ENV.ADMIN_PATH}/installment/plans`;

/**
 * 분할결제 계획 목록 조회
 */
export const getInstallmentPlans = async (
  query?: InstallmentPlanListQueryDto
): Promise<InstallmentPlanListResponseDto> => {
  const params: any = {
    page: query?.page || 1,
    limit: query?.limit || 10,
  };

  if (query) {
    if (query.comId) params.comId = query.comId;
    if (query.productId) params.productId = query.productId;
    if (query.planStatus) params.planStatus = query.planStatus;
    if (query.startDate) params.startDate = query.startDate;
    if (query.endDate) params.endDate = query.endDate;
    if (query.search) params.search = query.search;
  }

  return await instance.get(PLAN_PREFIX_PATH, { params });
};

/**
 * 분할결제 계획 상세 조회
 */
export const getInstallmentPlan = async (
  planId: number
): Promise<InstallmentPlanDto> => {
  return await instance.get(`${PLAN_PREFIX_PATH}/${planId}`);
};

/**
 * 분할결제 계획 취소
 */
export const cancelInstallmentPlan = async (
  planId: number
): Promise<ResponseDto> => {
  return await instance.patch(`${PLAN_PREFIX_PATH}/${planId}/cancel`);
};

/**
 * 분할결제 계획 일시정지
 */
export const pauseInstallmentPlan = async (
  planId: number
): Promise<ResponseDto> => {
  return await instance.patch(`${PLAN_PREFIX_PATH}/${planId}/pause`);
};

/**
 * 분할결제 계획 재개
 */
export const resumeInstallmentPlan = async (
  planId: number
): Promise<ResponseDto> => {
  return await instance.patch(`${PLAN_PREFIX_PATH}/${planId}/resume`);
};

/**
 * 분할결제 완료율 조회
 */
export const getPaymentCompletionRate = async (
  planId: number
): Promise<PaymentCompletionRateDto> => {
  return await instance.get(`${PLAN_PREFIX_PATH}/${planId}/completion-rate`);
};

// ===== 분할결제 이력 관리 API =====

/**
 * 분할계획별 결제 이력 조회
 */
export const getPaymentHistoryByPlan = async (
  planId: number,
  query?: InstallmentPaymentHistoryListQueryDto
): Promise<InstallmentPaymentHistoryListResponseDto> => {
  const params: any = {
    page: query?.page || 1,
    limit: query?.limit || 10,
  };

  if (query) {
    if (query.payStat) params.payStat = query.payStat;
    if (query.startDate) params.startDate = query.startDate;
    if (query.endDate) params.endDate = query.endDate;
  }

  return await instance.get(`${PLAN_PREFIX_PATH}/${planId}/payment-history`, {
    params,
  });
};

/**
 * 실패한 결제 이력 조회
 */
export const getFailedPayments = async (
  planId: number
): Promise<InstallmentPaymentHistoryDto[]> => {
  return await instance.get(`${PLAN_PREFIX_PATH}/${planId}/failed-payments`);
};

/**
 * 성공한 결제 이력 조회
 */
export const getSuccessfulPayments = async (
  planId: number
): Promise<InstallmentPaymentHistoryDto[]> => {
  return await instance.get(
    `${PLAN_PREFIX_PATH}/${planId}/successful-payments`
  );
};

/**
 * 대기 중인 결제 이력 조회
 */
export const getPendingPayments = async (
  planId: number
): Promise<InstallmentPaymentHistoryDto[]> => {
  return await instance.get(`${PLAN_PREFIX_PATH}/${planId}/pending-payments`);
};

// ===== 분할결제 설정 관리 API =====
const SETTING_PREFIX_PATH = `${ENV.PRE_FIX}/${ENV.ADMIN_PATH}/installment-setting`;

/**
 * 분할결제 설정 목록 조회
 */
export const getInstallmentSettings = async (
  query?: CompanyPaymentSettingListQueryDto
): Promise<CompanyPaymentSettingListResponseDto> => {
  const params: any = {
    page: query?.page || 1,
    limit: query?.limit || 10,
  };

  if (query) {
    if (query.comId) params.comId = query.comId;
    if (query.productId) params.productId = query.productId;
    if (query.allowInstallment)
      params.allowInstallment = query.allowInstallment;
    if (query.search) params.search = query.search;
  }

  return await instance.get(SETTING_PREFIX_PATH, { params });
};

/**
 * 분할결제 설정 상세 조회
 */
export const getInstallmentSetting = async (
  settingId: number
): Promise<CompanyPaymentSettingDto> => {
  return await instance.get(`${SETTING_PREFIX_PATH}/${settingId}`);
};

/**
 * 분할결제 설정 생성
 */
export const createInstallmentSetting = async (
  data: CreateCompanyPaymentSettingDto
): Promise<ResponseDto> => {
  return await instance.post(SETTING_PREFIX_PATH, data);
};

/**
 * 분할결제 설정 수정
 */
export const updateInstallmentSetting = async (
  settingId: number,
  data: UpdateCompanyPaymentSettingDto
): Promise<ResponseDto> => {
  return await instance.put(`${SETTING_PREFIX_PATH}/${settingId}`, data);
};

/**
 * 분할결제 설정 삭제
 */
export const deleteInstallmentSetting = async (
  settingId: number
): Promise<ResponseDto> => {
  return await instance.delete(`${SETTING_PREFIX_PATH}/${settingId}`);
};

/**
 * 도장별 분할결제 설정 조회
 */
export const getInstallmentSettingByCompany = async (
  comId: number,
  productId?: number
): Promise<CompanyPaymentSettingDto[]> => {
  const params: any = { comId };
  if (productId) params.productId = productId;

  return await instance.get(`${SETTING_PREFIX_PATH}/company`, { params });
};

// ===== 통계 관리 API =====
const STATISTICS_PREFIX_PATH = `${ENV.PRE_FIX}/${ENV.ADMIN_PATH}/installment/statistics`;

/**
 * 분할결제 통계 조회
 */
export const getInstallmentStatistics = async (
  startDate: string,
  endDate: string
): Promise<InstallmentPaymentStatisticsDto[]> => {
  return await instance.get(STATISTICS_PREFIX_PATH, {
    params: { startDate, endDate },
  });
};

/**
 * 월별 분할결제 현황 조회
 */
export const getMonthlyPaymentSummary = async (
  year: number
): Promise<MonthlyInstallmentSummaryDto[]> => {
  return await instance.get(
    `${STATISTICS_PREFIX_PATH}/monthly-summary/${year}`
  );
};

/**
 * 대시보드용 간단 통계
 */
export const getDashboardStats = async (): Promise<{
  totalActivePlans: number;
  totalCompletedPlans: number;
  thisMonthRevenue: number;
  successRate: number;
}> => {
  return await instance.get(`${STATISTICS_PREFIX_PATH}/dashboard`);
};

// 회사 스탬프 목록 조회
export const getCompanyStamps = async (params: {
  page?: number;
  limit?: number;
  comId?: number;
  templateId?: number;
  subscriptionStat?: string;
}): Promise<{
  list: CompanyStampDto[];
  count: number;
  page: number;
  limit: number;
}> => {
  const response = await instance.get(
    `${ENV.PRE_FIX}/${ENV.ADMIN_PATH}/company-stamps`,
    { params }
  );
  return response.data;
};

// 상품 목록 조회
export const getSubscriptionProducts = async (): Promise<{
  success: boolean;
  data: SubscriptionProductDto[];
}> => {
  const response = await instance.get(
    `${ENV.PRE_FIX}/${ENV.ADMIN_PATH}/subscription-products`
  );
  return response.data;
};

// 분할결제 이력 목록 조회
export const getInstallmentHistories = async (
  params: InstallmentHistoryListQueryDto
): Promise<ResultListDto<InstallmentHistoryDto>> => {
  const response = await axios.get(
    `${ENV.PRE_FIX}${ENV.ADMIN_PATH}/installment-histories`,
    {
      params,
    }
  );
  return response.data;
};

// 분할결제 이력 상세 조회
export const getInstallmentHistory = async (
  historyId: number
): Promise<InstallmentHistoryDto> => {
  const response = await axios.get(
    `${ENV.PRE_FIX}${ENV.ADMIN_PATH}/installment-histories/${historyId}`
  );
  return response.data;
};

// 분할결제 통계 조회
export const getInstallmentStats = async (
  params: InstallmentStatsQueryDto
): Promise<InstallmentStatsDto> => {
  const response = await axios.get(
    `${ENV.PRE_FIX}${ENV.ADMIN_PATH}/installment-stats`,
    { params }
  );
  return response.data;
};
